
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Navigation from '../../components/feature/Navigation';
import Card from '../../components/base/Card';
import Input from '../../components/base/Input';

interface Client {
  name: string;
  email: string;
  phone: string;
  jobCount: number;
  totalSpent: number;
  lastJobDate: string;
}

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Load jobs and extract unique clients
    const jobs = JSON.parse(localStorage.getItem('apostille_jobs') || '[]');
    const clientMap = new Map<string, Client>();

    jobs.forEach((job: any) => {
      const key = job.clientEmail || job.clientName;
      if (clientMap.has(key)) {
        const client = clientMap.get(key)!;
        client.jobCount += 1;
        client.totalSpent += job.totalAmount || 0;
        if (new Date(job.createdDate) > new Date(client.lastJobDate)) {
          client.lastJobDate = job.createdDate;
        }
      } else {
        clientMap.set(key, {
          name: job.clientName,
          email: job.clientEmail || '',
          phone: job.clientPhone || '',
          jobCount: 1,
          totalSpent: job.totalAmount || 0,
          lastJobDate: job.createdDate
        });
      }
    });

    setClients(Array.from(clientMap.values()));
  }, []);

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Clients" />
      
      <div className="pt-16 pb-20 px-4">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="p-3">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">{clients.length}</div>
              <div className="text-xs text-gray-600">Total Clients</div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">
                {clients.reduce((sum, client) => sum + client.jobCount, 0)}
              </div>
              <div className="text-xs text-gray-600">Total Jobs</div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="text-center">
              <div className="text-lg font-bold text-purple-600">
                ${clients.reduce((sum, client) => sum + client.totalSpent, 0).toFixed(0)}
              </div>
              <div className="text-xs text-gray-600">Total Revenue</div>
            </div>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6">
          <Input
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search clients..."
          />
        </div>

        {/* Clients List */}
        <div className="space-y-4">
          {filteredClients.length === 0 ? (
            <Card className="p-8">
              <div className="text-center text-gray-500">
                <i className="ri-user-line text-4xl mb-2"></i>
                <p className="text-sm">No clients found</p>
                <p className="text-xs text-gray-400 mt-1">
                  {clients.length === 0 ? 'Create your first job to see clients here' : 'Try adjusting your search'}
                </p>
              </div>
            </Card>
          ) : (
            filteredClients.map((client, index) => (
              <Card key={index} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{client.name}</h3>
                    {client.email && (
                      <p className="text-sm text-gray-600">{client.email}</p>
                    )}
                    {client.phone && (
                      <p className="text-sm text-gray-600">{client.phone}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Last job: {new Date(client.lastJobDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {client.jobCount} job{client.jobCount !== 1 ? 's' : ''}
                    </div>
                    <div className="text-sm text-green-600 font-medium">
                      ${client.totalSpent.toFixed(2)}
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      <Navigation />
    </div>
  );
}
